import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * UsageBreakdown — a hover card that explains a token or cost figure by breaking
 * it down per model and per usage category (input, output, cache read/write,
 * reasoning, audio, image, plus any custom team category). Fed by the spans'
 * `usage_details` / `cost_details` JSONB (per-category counts / USD); falls back
 * to the scalar input_tokens/output_tokens/cost when the details maps are absent.
 *
 *   <UsageHoverCard spans={spans} mode="tokens">{triggerNode}</UsageHoverCard>
 *
 * The card is portaled to <body> with fixed positioning so it is never clipped
 * by an ancestor's overflow or trapped under another stacking context.
 */

// Canonical category order + labels (mirrors backend usageCategories.js).
const CATEGORY_ORDER = ['input', 'output', 'cache_read', 'cache_write', 'reasoning', 'audio', 'image'];
const CATEGORY_LABEL = {
  input: 'Input',
  output: 'Output',
  cache_read: 'Cache read',
  cache_write: 'Cache write',
  reasoning: 'Reasoning',
  audio: 'Audio',
  image: 'Image',
};
function catLabel(k) {
  return CATEGORY_LABEL[k] || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function orderCats(keys) {
  const known = CATEGORY_ORDER.filter((k) => keys.includes(k));
  const custom = keys.filter((k) => !CATEGORY_ORDER.includes(k)).sort();
  return [...known, ...custom];
}

function fmtInt(n) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.round(v).toLocaleString() : '0';
}
function fmtUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '$0';
  if (v < 0.000001) return '<$0.000001';
  return `$${v.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
}

/**
 * Aggregate an array of spans into per-model usage. Returns:
 *   [{ model, provider, tokens: {cat: count}, costs: {cat: usd}, cost, inOut }]
 * sorted by cost desc then token volume desc. `inOut` = {input, output} top-line.
 */
export function aggregateUsageByModel(spans) {
  const byModel = new Map();
  (spans || []).forEach((s) => {
    const ud = s && s.usage_details && typeof s.usage_details === 'object' ? s.usage_details : null;
    const cd = s && s.cost_details && typeof s.cost_details === 'object' ? s.cost_details : null;
    const ti = Number(s?.input_tokens) || 0;
    const to = Number(s?.output_tokens) || 0;
    const sc = Number(s?.cost) || 0;
    const hasAny = ud || cd || ti > 0 || to > 0 || sc > 0;
    if (!hasAny) return;

    const model = s.model || 'unknown';
    if (!byModel.has(model)) {
      byModel.set(model, { model, provider: s.provider || null, tokens: {}, costs: {}, cost: 0 });
    }
    const g = byModel.get(model);
    if (!g.provider && s.provider) g.provider = s.provider;

    // Token categories — prefer the details map, else the scalar in/out.
    if (ud) {
      for (const [k, v] of Object.entries(ud)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) g.tokens[k] = (g.tokens[k] || 0) + n;
      }
    } else {
      if (ti > 0) g.tokens.input = (g.tokens.input || 0) + ti;
      if (to > 0) g.tokens.output = (g.tokens.output || 0) + to;
    }

    // Cost categories — prefer the details map; always track the scalar total.
    if (cd) {
      for (const [k, v] of Object.entries(cd)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) g.costs[k] = (g.costs[k] || 0) + n;
      }
    }
    if (Number.isFinite(sc)) g.cost += sc;
  });

  return Array.from(byModel.values())
    .map((g) => ({ ...g, inOut: { input: g.tokens.input || 0, output: g.tokens.output || 0 } }))
    .sort((a, b) => (b.cost - a.cost) || ((b.inOut.input + b.inOut.output) - (a.inOut.input + a.inOut.output)));
}

function ModelSection({ g, mode, fmt }) {
  const map = mode === 'cost' ? g.costs : g.tokens;
  const cats = orderCats(Object.keys(map).filter((k) => Number(map[k]) > 0));
  const subtotal = mode === 'cost' ? g.cost : (g.inOut.input + g.inOut.output);
  return (
    <div>
      {/* Model header: name only (no provider), subtotal right-aligned */}
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate font-mono text-[11px] font-semibold text-[color:var(--text-primary)]" title={g.model}>
          {g.model}
        </span>
        <span className="flex-none tabular-nums text-[11px] font-semibold text-[color:var(--text-primary)]">
          {mode === 'cost' ? fmtUsd(subtotal) : `${fmtInt(subtotal)} tok`}
        </span>
      </div>
      {/* Category sub-rows: one per line, muted label / value right-aligned */}
      <div className="mt-1 space-y-0.5">
        {cats.length === 0 ? (
          <div className="text-[11px] text-[color:var(--text-muted)]">—</div>
        ) : cats.map((k) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-[color:var(--text-muted)]">{catLabel(k)}</span>
            <span className="flex-none tabular-nums text-[11px] font-medium text-[color:var(--text-secondary)]">{fmt(map[k])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BreakdownCard({ models, mode }) {
  // Grand totals across models for the header + footer.
  let totalIn = 0, totalOut = 0, totalCost = 0;
  models.forEach((g) => { totalIn += g.inOut.input; totalOut += g.inOut.output; totalCost += g.cost; });
  const fmt = mode === 'cost' ? fmtUsd : fmtInt;
  return (
    <div
      className="w-[260px] max-w-[88vw] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] shadow-token-lg"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="border-b border-[color:var(--border-subtle)] px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
          {mode === 'cost' ? 'Cost breakdown' : 'Token breakdown'}
        </span>
      </div>
      {/* Flat list of model sections: divided by hairlines, not boxed */}
      <div className="max-h-[320px] divide-y divide-[color:var(--border-subtle)] overflow-y-auto">
        {models.map((g) => (
          <div key={g.model} className="px-3 py-2">
            <ModelSection g={g} mode={mode} fmt={fmt} />
          </div>
        ))}
      </div>
      {/* Grand total: only meaningful when there's more than one model */}
      {models.length > 1 && (
        <div className="flex items-baseline justify-between gap-3 border-t border-[color:var(--border-subtle)] px-3 py-1.5">
          <span className="text-[11px] font-semibold text-[color:var(--text-primary)]">Total</span>
          <span className="tabular-nums text-[11px] font-semibold text-[color:var(--text-primary)]">
            {mode === 'cost' ? fmtUsd(totalCost) : `${fmtInt(totalIn)} → ${fmtInt(totalOut)}`}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Wrap a value with a hover card breaking it down by model/category.
 *   mode: 'tokens' | 'cost'
 *   spans: the spans to aggregate (the whole run, or a single span's subtree)
 * Renders nothing special if there's no usable breakdown — just the children.
 */
export default function UsageHoverCard({ spans, mode = 'tokens', children, className = '', underline = true }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const models = React.useMemo(() => aggregateUsageByModel(spans), [spans]);
  const hasBreakdown = models.length > 0;

  const open = useCallback(() => {
    if (!hasBreakdown || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    // Prefer below the trigger; flip above if there isn't room.
    const below = window.innerHeight - r.bottom;
    const placeAbove = below < 200 && r.top > below;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - 308);
    setPos({ left, top: placeAbove ? null : r.bottom + 6, bottom: placeAbove ? (window.innerHeight - r.top + 6) : null });
  }, [hasBreakdown]);

  const close = useCallback(() => setPos(null), []);

  return (
    <span
      ref={ref}
      onMouseEnter={open}
      onMouseLeave={close}
      className={`${hasBreakdown ? `cursor-help ${underline ? 'decoration-dotted underline-offset-2 hover:underline' : ''}` : ''} ${className}`}
    >
      {children}
      {pos && createPortal(
        <div
          className="pointer-events-none fixed z-[130]"
          style={{ left: pos.left, top: pos.top ?? undefined, bottom: pos.bottom ?? undefined }}
        >
          <BreakdownCard models={models} mode={mode} />
        </div>,
        document.body,
      )}
    </span>
  );
}
